/**
 * Pattern Analysis Tab — "Where and when is activity concentrated?"
 *
 * Temporal heatmap (day x hour with REAL data), activity trend line chart,
 * spatial heatmap (PostGIS grid), and repeat visitor detection.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MapIcon,
  ClockIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import {
  intelligenceApi,
  type TimeRange,
  getGranularity,
} from '../../services/analyticsService';
import { SkeletonChart } from '../common/Skeleton';
import SpatialHeatmapMap from './SpatialHeatmapMap';
// Phase 8 follow-up: dropped the redundant <FlightsByPeriodCard /> below.
// It overlapped with the Temporal Heatmap (Day × Hour) + Activity Trend
// hour-of-day bar chart already on this tab — operators couldn't tell the
// three views apart. Kept the file in case a future analytical view wants
// a true chronological time-series.

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 24 }, (_, i) =>
  `${i.toString().padStart(2, '0')}`
);

interface PatternTabProps {
  tenantId: string;
  timeRange: TimeRange;
  startTime: string;
  endTime: string;
  analyticsAvailable: boolean | null;
  onInvestigateTrack: (trackId: string) => void;
}

export default function PatternTab({
  tenantId,
  timeRange,
  startTime,
  endTime,
  analyticsAvailable,
}: PatternTabProps) {
  // Hovered heatmap cell — drives the live readout above the grid. We
  // can't rely on the HTML title attribute alone because it has a
  // browser-controlled delay before showing.
  const [hoveredCell, setHoveredCell] = useState<{
    dayLabel: string;
    hourLabel: string;
    count: number;
  } | null>(null);

  // ── Hourly trend fetch ────────────────────────────────────────────────
  // Always use 'hour' granularity (regardless of the tab's timeRange)
  // because both the Day×Hour heatmap and the Hour-of-Day bar chart need
  // hour-level bucketing. /trends counts COUNT(DISTINCT track_id) per hour
  // from the raw tracks table, so every position is represented — unlike
  // the /tracks endpoint which returns just the last position per track.
  const { data: hourlyTrend, isLoading: hourlyTrendLoading } = useQuery({
    queryKey: ['intelligence', 'trends-hourly', tenantId, startTime, endTime],
    queryFn: () =>
      intelligenceApi.getTrends(tenantId, startTime, endTime, 'hour'),
    enabled: analyticsAvailable === true && !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // Overall trend (matches timeRange granularity) — used for the small
  // "activity is increasing/decreasing" summary under the hour-of-day chart.
  const { data: trendData } = useQuery({
    queryKey: ['intelligence', 'trends-pattern', tenantId, timeRange],
    queryFn: () =>
      intelligenceApi.getTrends(
        tenantId,
        startTime,
        endTime,
        getGranularity(timeRange)
      ),
    enabled: analyticsAvailable === true && !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch spatial heatmap
  const { data: heatmapData, isLoading: heatmapLoading } = useQuery({
    queryKey: ['intelligence', 'heatmap', tenantId, timeRange],
    queryFn: () =>
      intelligenceApi.getHeatmap(tenantId, startTime, endTime, {
        resolution: 0.01,
      }),
    enabled: analyticsAvailable === true && !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // ── Day × Hour temporal heatmap (from hourly trend data) ──────────────
  // Each data point is one hour with unique_tracks count. Sum into the
  // (day-of-week, hour-of-day) cell.
  const temporalGrid = useMemo(() => {
    const grid: number[][] = DAYS_OF_WEEK.map(() => HOURS.map(() => 0));
    const points = hourlyTrend?.data_points || [];
    points.forEach((dp) => {
      if (!dp.period) return;
      const d = new Date(dp.period);
      const dayIdx = (d.getDay() + 6) % 7; // Mon=0, Sun=6
      const hourIdx = d.getHours();
      grid[dayIdx][hourIdx] += dp.unique_tracks || 0;
    });
    return grid;
  }, [hourlyTrend]);

  const temporalMax = useMemo(
    () => Math.max(...temporalGrid.flat(), 1),
    [temporalGrid]
  );

  const anyTemporalActivity = useMemo(
    () => temporalGrid.some((row) => row.some((v) => v > 0)),
    [temporalGrid],
  );

  // ── Hour-of-day bar chart (from hourly trend data) ────────────────────
  // Sum unique_tracks per hour across all days in the window.
  const trendByHourOfDay = useMemo(() => {
    const perHour = Array.from({ length: 24 }, () => 0);
    const points = hourlyTrend?.data_points || [];
    points.forEach((dp) => {
      if (!dp.period) return;
      const hour = new Date(dp.period).getHours();
      perHour[hour] += dp.unique_tracks || 0;
    });
    return perHour.map((tracks, hour) => ({
      hour: `${hour.toString().padStart(2, '0')}:00`,
      tracks,
    }));
  }, [hourlyTrend]);

  const peakHour = useMemo(() => {
    if (!trendByHourOfDay.length) return null;
    return trendByHourOfDay.reduce((best, cur) =>
      cur.tracks > best.tracks ? cur : best,
    );
  }, [trendByHourOfDay]);

  const anyTrendActivity = useMemo(
    () => trendByHourOfDay.some((d) => d.tracks > 0),
    [trendByHourOfDay],
  );

  return (
    <div className="space-y-6">
      {/* Temporal Heatmap */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <ClockIcon className="h-5 w-5 text-accent" />
          <h3 className="text-lg font-display font-semibold text-white">
            Temporal Activity Heatmap
          </h3>
          <span className="text-xs text-gray-500 ml-2">Day &times; Hour</span>
          {/* Live readout for the currently-hovered cell. Sits at the
              right of the header so it doesn't push the legend / grid
              around when it appears or disappears. */}
          {hoveredCell && (
            <span className="ml-auto text-xs font-mono text-gray-300 bg-navy-800/80 border border-navy-700 rounded px-2 py-1">
              {hoveredCell.dayLabel} {hoveredCell.hourLabel}:00 &mdash;{' '}
              <strong className="text-accent">
                {hoveredCell.count.toLocaleString()}
              </strong>{' '}
              flight{hoveredCell.count === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {analyticsAvailable === false ? (
          <div className="h-40 flex items-center justify-center text-sm text-gray-500">
            Analytics service required for temporal activity breakdown
          </div>
        ) : hourlyTrendLoading ? (
          <SkeletonChart />
        ) : !anyTemporalActivity ? (
          <div className="h-40 flex items-center justify-center text-sm text-gray-500">
            No activity detected in the selected window
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              {/* Hour labels */}
              <div className="flex mb-1 ml-10">
                {HOURS.filter((_, i) => i % 3 === 0).map((h) => (
                  <div
                    key={h}
                    className="text-[10px] text-gray-500 text-center"
                    style={{ width: `${(3 / 24) * 100}%` }}
                  >
                    {h}:00
                  </div>
                ))}
              </div>
              {/* Rows */}
              {DAYS_OF_WEEK.map((day, dayIdx) => (
                <div key={day} className="flex items-center gap-1 mb-1">
                  <div className="w-9 text-[11px] text-gray-500 text-right pr-1 flex-shrink-0">
                    {day}
                  </div>
                  <div className="flex-1 flex gap-[2px]">
                    {temporalGrid[dayIdx].map((value, hourIdx) => {
                      const intensity = value / temporalMax;
                      return (
                        <div
                          key={hourIdx}
                          className="flex-1 h-5 rounded-sm cursor-pointer transition-opacity hover:opacity-80 hover:ring-1 hover:ring-accent/40"
                          style={{
                            backgroundColor: `rgba(255, 181, 71, ${
                              0.05 + intensity * 0.7
                            })`,
                          }}
                          title={`${day} ${HOURS[hourIdx]}:00 — ${value} flight${value === 1 ? '' : 's'}`}
                          onMouseEnter={() =>
                            setHoveredCell({
                              dayLabel: day,
                              hourLabel: HOURS[hourIdx],
                              count: value,
                            })
                          }
                          onMouseLeave={() => setHoveredCell(null)}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
              {/* Legend */}
              <div className="flex items-center justify-end gap-2 mt-3 mr-1">
                <span className="text-[10px] text-gray-500">Less</span>
                {[0.1, 0.3, 0.5, 0.7, 0.9].map((v) => (
                  <div
                    key={v}
                    className="w-4 h-4 rounded-sm"
                    style={{
                      backgroundColor: `rgba(255, 181, 71, ${0.05 + v * 0.7})`,
                    }}
                  />
                ))}
                <span className="text-[10px] text-gray-500">More</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Trend Analysis + Spatial Heatmap (side by side) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Trend — Time of Day distribution */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <ChartBarIcon className="h-5 w-5 text-sky" />
            <h3 className="text-lg font-display font-semibold text-white">
              Activity Trend
            </h3>
            <span className="text-xs text-gray-500 ml-2">
              Unique tracks by hour of day
            </span>
          </div>

          {analyticsAvailable === false ? (
            <div className="h-48 flex items-center justify-center text-sm text-gray-500">
              Analytics service required for hour-of-day trend
            </div>
          ) : hourlyTrendLoading ? (
            <SkeletonChart />
          ) : anyTrendActivity ? (
            <>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendByHourOfDay}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#2c4567"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="hour"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7d97', fontSize: 11 }}
                      interval={2}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7d97', fontSize: 11 }}
                      width={30}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#142a4a',
                        border: '1px solid #2c4567',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '12px',
                      }}
                      cursor={{ fill: 'rgba(255, 181, 71, 0.08)' }}
                    />
                    <Bar
                      dataKey="tracks"
                      fill="#ffb547"
                      radius={[3, 3, 0, 0]}
                      name="Unique Tracks"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {peakHour && peakHour.tracks > 0 && (
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                  <ArrowTrendingUpIcon className="h-4 w-4 text-accent" />
                  <span>
                    Peak activity around{' '}
                    <span className="text-accent font-semibold">
                      {peakHour.hour}
                    </span>{' '}
                    ({peakHour.tracks} unique track
                    {peakHour.tracks === 1 ? '' : 's'}).
                  </span>
                </div>
              )}
              {analyticsAvailable && trendData?.summary && (
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                  {trendData.summary.trend === 'increasing' ? (
                    <ArrowTrendingUpIcon className="h-3.5 w-3.5 text-red-400" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-3.5 w-3.5 text-accent" />
                  )}
                  <span>
                    Overall {timeRange} window is{' '}
                    <span
                      className={
                        trendData.summary.trend === 'increasing'
                          ? 'text-red-400'
                          : 'text-accent'
                      }
                    >
                      {trendData.summary.trend}
                    </span>{' '}
                    · avg{' '}
                    {Math.round(trendData.summary.avg_tracks_per_period)}{' '}
                    tracks/{getGranularity(timeRange)}.
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="h-48 flex items-center justify-center text-sm text-gray-500">
              No activity in the selected window
            </div>
          )}
        </div>

        {/* Spatial Heatmap — on a real map */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <MapIcon className="h-5 w-5 text-purple-400" />
            <h3 className="text-lg font-display font-semibold text-white">
              Spatial Activity Heatmap
            </h3>
            {heatmapData?.cells && (
              <span className="text-xs text-gray-500 ml-2">
                {heatmapData.cells.filter((c) => c.count > 0).length} active cells
              </span>
            )}
          </div>

          {analyticsAvailable === false ? (
            <div className="h-48 flex items-center justify-center text-sm text-gray-500">
              Analytics service required for spatial heatmap
            </div>
          ) : heatmapLoading ? (
            <SkeletonChart />
          ) : heatmapData &&
            heatmapData.cells &&
            heatmapData.cells.some((c) => c.count > 0) ? (
            <SpatialHeatmapMap heatmap={heatmapData} height={380} />
          ) : (
            <div className="h-48 flex items-center justify-center text-sm text-gray-500">
              No spatial data available for this period
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
